import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { LucideIcon } from "lucide-react";
import {
  Camera,
  Mic,
  Upload,
  BarChart3,
  FileText,
  Sparkles,
  Link2,
  Pencil,
  User,
  Images,
  ChevronDown,
  LayoutGrid,
  Loader2,
  Utensils,
  Activity,
  ClipboardList,
  NotebookPen,
  Clock3,
  Plus,
  Dumbbell,
  HeartPulse,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCPF, hasValidCpfDigits } from "@/lib/formatters";
import { useNavigate, Link } from "react-router-dom";
import { isAIDisabled, setAIDisabled } from "@/lib/smartfile-ai-preference";
import {
  usePatientRecentApps,
  type PatientRecentAppKind,
} from "@/hooks/usePatientRecentApps";

function recentAppIcon(kind: PatientRecentAppKind): LucideIcon {
  switch (kind) {
    case "meal_plan":
      return Utensils;
    case "pilates_plan":
      return Activity;
    case "prescription":
      return FileText;
    case "clinical_record":
      return ClipboardList;
    case "pilates_evolution":
      return NotebookPen;
    case "training_plan":
      return Dumbbell;
    case "heart_risk":
      return HeartPulse;
    default:
      return LayoutGrid;
  }
}

interface Patient {
  id: string;
  name: string;
  cpf: string;
  birth_date: string | null;
  email: string | null;
  phone: string | null;
  avatar_url?: string | null;
}

interface PatientPageHeaderProps {
  patient: Patient;
  onBack?: () => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onCameraClick?: () => void;
  onAudioClick?: () => void;
  onFileClick?: () => void;
  onTextClick?: () => void;
  isProcessing?: boolean;
  progressStage?: string;
  progressValue?: number;
  /** Controla abertura da seção Dados de Saúde (ex.: abrir ao processar) */
  captureOpen?: boolean;
  onCaptureOpenChange?: (open: boolean) => void;
  basePath?: "/app" | "/paciente";
  availableTabs?: string[];
  showRecentApps?: boolean;
}

/** Idade em anos para exibição, ou null se não houver data válida. */
const getDisplayableAgeYears = (birthDate: string | null): number | null => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  if (age < 0 || age > 120) return null;
  return age;
};

export const patientTabs = [
  { value: "timeline", label: "Timeline", icon: Clock3 },
  { value: "indicators", label: "Indicadores", icon: BarChart3 },
  { value: "vision360", label: "Visão 360", icon: User },
  { value: "registros", label: "Registros", icon: FileText },
  { value: "galeria", label: "Galeria", icon: Images },
  { value: "coleta", label: "Links", icon: Link2 },
  { value: "insights", label: "Análises IA", icon: Sparkles },
];

export function PatientPageHeader({
  patient,
  onBack: _onBack,
  activeTab = "indicators",
  onTabChange,
  onCameraClick,
  onAudioClick,
  onFileClick,
  onTextClick,
  isProcessing = false,
  progressStage = "",
  progressValue = 0,
  captureOpen: captureOpenControlled,
  onCaptureOpenChange,
  basePath = "/app",
  availableTabs,
  showRecentApps = true,
}: PatientPageHeaderProps) {
  const initials = patient.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  const navigate = useNavigate();
  const [disableAI, setDisableAI] = useState(() => isAIDisabled());
  const [captureOpenInternal, setCaptureOpenInternal] = useState(true);

  const captureOpen =
    captureOpenControlled !== undefined ? captureOpenControlled : captureOpenInternal;
  const setCaptureOpen = onCaptureOpenChange ?? setCaptureOpenInternal;

  const { data: recentApps = [], isLoading: recentAppsLoading } = usePatientRecentApps(
    patient.id,
  );

  useEffect(() => {
    setAIDisabled(disableAI);
  }, [disableAI]);

  const captureCardClass =
    "border border-border/50 shadow-sm rounded-lg bg-card/50 transition-all duration-200";

  const goSmartfile = (hash: string) => {
    window.location.href = `${basePath}/smartfile?patientId=${patient.id}${hash}`;
  };

  const tabsToRender = availableTabs
    ? patientTabs.filter((tab) => availableTabs.includes(tab.value))
    : patientTabs;

  const displayAgeYears = getDisplayableAgeYears(patient.birth_date);
  const showCpf = hasValidCpfDigits(patient.cpf);
  const showAge = displayAgeYears !== null;
  const captureActions: Array<{
    id: string;
    label: string;
    Icon: LucideIcon;
    onClick: () => void;
  }> = [
    {
      id: "camera",
      label: "Câmera",
      Icon: Camera,
      onClick: onCameraClick || (() => goSmartfile("#camera")),
    },
    {
      id: "audio",
      label: "Áudio",
      Icon: Mic,
      onClick: onAudioClick || (() => goSmartfile("#audio")),
    },
    {
      id: "upload",
      label: "Arquivo",
      Icon: Upload,
      onClick: onFileClick || (() => goSmartfile("#upload")),
    },
    {
      id: "text",
      label: "Texto",
      Icon: FileText,
      onClick: onTextClick || (() => goSmartfile("#text")),
    },
  ];

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden">
      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 w-full max-w-full">
        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0 w-full">
          <Avatar className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0">
            {patient.avatar_url && <AvatarImage src={patient.avatar_url} alt={patient.name} />}
            <AvatarFallback className="bg-primary/10 text-primary">{initials}</AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col gap-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold tracking-tight truncate">
                  {patient.name}
                </h1>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`${basePath}/pacientes/${patient.id}/editar`)}
                  className="h-8 px-2 flex-shrink-0"
                  title="Editar paciente"
                >
                  <Pencil className="h-4 w-4 mr-1.5" />
                  Editar
                </Button>
              </div>

              <div className="flex flex-col gap-y-1 mt-1 text-xs sm:text-sm text-muted-foreground">
                {showCpf && (
                  <span className="flex items-center gap-1 truncate">
                    <strong className="flex-shrink-0">CPF:</strong>
                    <span className="truncate">{formatCPF(patient.cpf)}</span>
                  </span>
                )}
                {showAge && (
                  <span className="flex items-center gap-1 flex-shrink-0">
                    <strong>Idade:</strong> {displayAgeYears} anos
                  </span>
                )}
                {patient.phone && (
                  <span className="flex items-center gap-1 truncate">
                    <strong className="flex-shrink-0">Celular:</strong>
                    <span className="truncate">{patient.phone}</span>
                  </span>
                )}
                {patient.email && (
                  <span className="flex items-center gap-1 truncate">
                    <strong className="flex-shrink-0">E-mail:</strong>
                    <span className="truncate">{patient.email}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2 w-full">
        <Collapsible open={captureOpen} onOpenChange={setCaptureOpen}>
          <div className={captureCardClass}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left rounded-lg transition-colors sm:hover:bg-muted/40"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <Sparkles className="h-4 w-4 text-primary shrink-0 mt-1" />
                  <div className="min-w-0">
                    <span className="text-sm font-semibold block">Dados de Saúde</span>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Insira os dados de saúde nos formatos abaixo
                    </p>
                  </div>
                </div>
                <ChevronDown
                  className={cn(
                    "hidden sm:block h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200",
                    captureOpen && "rotate-180",
                  )}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 pb-4 pt-1 space-y-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-5 max-w-[240px] sm:max-w-none sm:flex sm:flex-wrap sm:items-start sm:gap-4 animate-in slide-in-from-top-2 duration-200">
                  {captureActions.map(({ id, label, Icon, onClick }) => (
                    <Button
                      key={id}
                      variant="ghost"
                      onClick={onClick}
                      className="group inline-flex w-[92px] h-auto flex-col items-center gap-2 p-0 hover:bg-transparent disabled:opacity-50"
                      size="sm"
                      disabled={isProcessing}
                    >
                      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                        <Icon className="h-6 w-6" aria-hidden />
                      </span>
                      <span className="text-[12px] sm:text-[13px] font-semibold leading-tight text-center text-foreground line-clamp-2">
                        {label}
                      </span>
                    </Button>
                  ))}
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="disable-ai-header"
                    checked={disableAI}
                    onCheckedChange={(checked) => setDisableAI(checked === true)}
                    className="rounded-full border-primary/50 data-[state=checked]:border-primary"
                  />
                  <Label
                    htmlFor="disable-ai-header"
                    className="text-[12px] sm:text-[13px] font-semibold text-muted-foreground cursor-pointer leading-tight"
                  >
                    Desativar Resumo e Insights IA
                  </Label>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {showRecentApps && (
          <div className={captureCardClass}>
            <div className="px-3 py-3">
              <div className="flex items-start gap-3 min-w-0 mb-3">
                <LayoutGrid className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <span className="text-sm font-semibold block text-foreground">Aplicativos</span>
                </div>
              </div>

              {recentAppsLoading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Carregando...
                </div>
              )}

              {!recentAppsLoading && (
                <div className="flex flex-wrap items-start gap-4">
                  {recentApps.length > 0 &&
                    recentApps.map((item) => {
                      const AppIcon = recentAppIcon(item.kind);
                      return (
                        <Link
                          key={item.kind}
                          to={item.href}
                          className="group inline-flex w-[86px] flex-col items-center gap-2"
                        >
                          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                            <AppIcon className="h-6 w-6" aria-hidden />
                          </span>
                          <span className="text-[13px] font-semibold leading-tight text-center text-foreground line-clamp-2">
                            {item.label}
                          </span>
                        </Link>
                      );
                    })}

                  <Link
                    to={`${basePath}/aplicativos`}
                    className="group inline-flex w-[86px] flex-col items-center gap-2"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/5 text-primary transition-colors group-hover:bg-primary/10">
                      <Plus className="h-6 w-6" aria-hidden />
                    </span>
                    <span className="text-[13px] font-semibold leading-tight text-center text-foreground">
                      Adicionar
                    </span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {isProcessing && progressStage && (
        <div className="w-full space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground font-medium">{progressStage}</span>
            <span className="text-muted-foreground">{progressValue}%</span>
          </div>
          <Progress value={progressValue} className="h-2" />
        </div>
      )}

      <div className="border-t pt-3 pb-2 w-full max-w-full overflow-x-hidden">
        <div className="lg:hidden">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6">
            {tabsToRender.map((tab) => {
              const Icon = tab.icon;
              return (
                <Badge
                  key={tab.value}
                  variant={activeTab === tab.value ? "default" : "outline"}
                  className={cn(
                    "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
                    activeTab === tab.value
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "hover:bg-accent",
                  )}
                  onClick={() => onTabChange?.(tab.value)}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="font-medium">{tab.label}</span>
                </Badge>
              );
            })}
          </div>
        </div>

        <div className="hidden lg:flex gap-2 flex-wrap">
          {tabsToRender.map((tab) => {
            const Icon = tab.icon;
            return (
              <Badge
                key={tab.value}
                variant={activeTab === tab.value ? "default" : "outline"}
                className={cn(
                  "shrink-0 px-4 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-2",
                  activeTab === tab.value
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "hover:bg-accent",
                )}
                onClick={() => onTabChange?.(tab.value)}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
              </Badge>
            );
          })}
        </div>
      </div>
    </div>
  );
}
